import React, { useEffect, useState } from "react";
import SummaryApi from "../common";
import { useNavigate } from "react-router-dom";

const RelatedPrograms = () => {
  const [careerCourses, setCareerCourses] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(SummaryApi.getActiveCarrerCourses.url)
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          const filtered = data.data.filter((course) => course.is_career_course === 1);
          setCareerCourses(filtered);
        }
      })
      .catch((error) => console.error("API fetch error:", error));
  }, []);

  const extractLearningPoints = (htmlString) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;
    const listItems = tempDiv.querySelectorAll("li");
    if (listItems.length > 0) {
      return Array.from(listItems).map((li) => li.textContent.trim());
    } else {
      const paragraphs = tempDiv.querySelectorAll("p");
      return Array.from(paragraphs).map((p) => p.textContent.trim()).filter(Boolean);
    }
  };

  const primaryCourses = careerCourses.slice(0, 4);
  const secondaryCourses = showAll
    ? careerCourses.slice(4)
    : careerCourses.slice(4, 7); // Only 3 if not showing all

  return (
    <section className="bg-[#f5f9ff] dark:bg-gray-900 py-16 px-6">
      <div className="text-center mb-12">
        <h2 className="text-[48px] font-extrabold text-[#1c3664] dark:text-white mb-4 text-inter">
          Career Related Programs
        </h2>
        <p className="text-[20px] text-[#1c3664] dark:text-gray-300 max-w-4xl mx-auto font-medium">
          Comprehensive master programs designed to accelerate your career
          growth with industry partnerships and certifications.
        </p>
      </div>

      {/* Primary Design Cards (First 4) */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 max-w-7xl mx-auto mb-20">
        {primaryCourses.map((course) => {
          const learningPoints = extractLearningPoints(course.student_learning);
          return (
            <div
              key={course.course_id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-100 dark:border-gray-700 border-l-4 border-l-[#163165] dark:border-l-[#1e40af] p-6 flex flex-col justify-between"
            >
              <div className="flex-1 flex flex-col justify-between mt-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-bold text-lg text-[#163165] dark:text-white">
                    {course.course_title}
                  </h3>
                  <span className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-[10px] px-2 py-1 rounded">
                    {course.rating}★
                  </span>
                </div>

                {/* Learning Points */}
                <div className="mt-4 mb-2">
                  <p className="font-semibold text-sm mb-2 text-gray-700 dark:text-gray-300">
                    What you'll learn
                  </p>
                  <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                    {learningPoints.slice(0, 3).map((point, i) => (
                      <li key={i} className="flex items-start">
                        <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <button 
                onClick={() => {
                  const encodedId = btoa(course.course_id.toString());
                  navigate(`/all-courses/${encodedId}`);
                }}
                className="mt-auto text-white w-full py-1 rounded-md font-semibold bg-[#1C3664] hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-700 transition"
              >
                View Details
              </button>
            </div>
          );
        })}
      </div>

      {/* Secondary Design Cards (Remaining) */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto">
        {secondaryCourses.map((course) => {
          const learningPoints = extractLearningPoints(course.student_learning);
          return (
            <div
              key={course.course_id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md relative p-6 pb-4 border border-gray-200 dark:border-gray-700 after:content-[''] after:absolute after:bottom-0 after:right-0 after:w-full after:h-full after:border-r-[10px] after:border-b-[10px] after:border-r-[#2d63dd] after:border-b-[#2d63dd] after:rounded-lg after:z-0"
            >
              <div className="relative z-10">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-[#1c3664] dark:text-white leading-tight text-[16px] max-w-[80%]">
                    {course.course_title}
                  </h3>
                  <span className="text-[10px] text-white bg-[#2d63dd] dark:bg-blue-600 px-2 py-1 rounded font-medium">
                    {course.rating}★
                  </span>
                </div>

                {/* Learning Points */}
                <div className="mt-4 mb-3">
                  <p className="font-bold text-sm text-[#1c3664] dark:text-white mb-2">
                    What you’ll learn
                  </p>
                  <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                    {learningPoints.slice(0, 3).map((point, i) => (
                      <li key={i} className="flex items-start">
                        <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>

                <button className="mt-2 bg-[#163165] dark:bg-blue-700 text-white w-full py-1 rounded-md font-semibold hover:bg-[#12284e] dark:hover:bg-blue-800 transition">
                  See More
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* View More Button */}
      {careerCourses.length > 7 && !showAll && (
        <div className="text-center mt-10">
          <button
            onClick={() => navigate('/carrer-courses')}  // <-- Navigate here
            className="px-6 py-3 bg-[#163165] dark:bg-blue-600 text-white rounded-md font-semibold hover:bg-[#234caa] dark:hover:bg-blue-700 transition"
          >
            View More Programs
          </button>
        </div>
      )}
    </section>
  );
};

export default RelatedPrograms;